import type { ComponentProps } from 'react';
import { useEffect, useState } from 'react';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { Chip, IconButton, Menu, MenuItem, TableCell, TableRow, Typography } from '@mui/material';
import { useGetListDailyCheckinEventMutation, useDeleteDailyCheckinMutation, useDisableDailyCheckinMutation } from 'store/dailycheckin';
import Content from 'components/content/content';
import Icon from 'components/icon';
import Notification from 'components/notification';
import GLOBAL from 'modules/global';
import CONSTANT from 'modules/constant';
import type { DailyCheckinEventProps, ErrorProps } from 'modules/types';
import MenuModalItem from "components/menuItem/menuItem";
import Create from "./create/create";
import Edit from "./edit/edit";
import Detail from "./view/detail";

const DailyCheckinList = () => {
  const [getDailyCheckinList, dailyCheckinList] = useGetListDailyCheckinEventMutation();
  const [deleteDailyCheckin, deleteResult] = useDeleteDailyCheckinMutation();
  const [disableDailyCheckin, disableResult] = useDisableDailyCheckinMutation();
  const [dialogCreate, setDialogCreate] = useState(false);
  const [dialogUpdate, setDialogUpdate] = useState(false);
  const [dialogDetail, setDialogDetail] = useState(false);
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const [menu, setMenu] = useState<null | number>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const [payload, setPayload] = useState({
    start: 0,
    length: CONSTANT.DEFAULT_PAGINATION_PER_PAGE,
    Search: '',
  });

  const formatDate = (e: string) => (!isEmpty(e) ? moment(e).format('DD-MM-YYYY HH:mm') : '-');

  const onSearch = (value: string) => setPayload({ ...payload, start: 0, Search: value });
  const onChangePage = (value: number) => setPayload({ ...payload, start: value - 1 });
  const onChangePerPage = (value: number) => setPayload({ ...payload, length: value });

  const onAction = (event: React.MouseEvent<HTMLElement>, index: number, id: number) => {
    setAnchor(event.currentTarget);
    setMenu(index);
    setSelectedId(id);
  };

  const onRemoveAnchor = (callback?: CallableFunction) => {
    setAnchor(null);
    callback && callback();
  };

  const onResync = () => {
    const formData = new FormData();
    formData.append('start', payload.start.toString());
    formData.append('length', payload.length.toString());
    if (!isEmpty(payload.Search)) formData.append('Search', payload.Search);
    getDailyCheckinList(formData);
  };

  const columns = [
    { column: 'Description', label: 'Description', key: 'description' },
    { column: 'Banner Image', label: 'Banner Image', key: 'bannerImage', render: (row: DailyCheckinEventProps) => row.bannerImage?.split('/').slice(4).join('/') || '-' },
    { column: 'Period Start', label: 'Period Start', key: 'periodStart', render: (row: DailyCheckinEventProps) => formatDate(row.periodStart) },
    { column: 'Period End', label: 'Period End', key: 'periodEnd', render: (row: DailyCheckinEventProps) => formatDate(row.periodEnd) },
    { column: 'Consecutive Days', label: 'Consecutive Days', key: 'consecutiveDays' },
    { column: 'Rewards', label: 'Rewards', key: 'rewards' },
    {
      column: 'Status', label: 'Status', key: 'status', render: (row: DailyCheckinEventProps) => {
        const status = row.status;
        let color: ComponentProps<typeof Chip>['color'] = 'default';
        if (status === 'Active') {
          color = 'success';
        } else if (status === 'Not Started') {
          color = 'info';
        } else if (status === 'Ended') {
          color = 'error';
        } else {
          color = 'default';
        }
        return <Chip label={status} color={color} />;
      }
    },
    { column: 'Created At', label: 'Created At', key: 'createdAt', render: (row: DailyCheckinEventProps) => formatDate(row.createdAt) },
  ];

  const tableHeadTitles = columns.map(c => c.label);
  const totalPage = dailyCheckinList.data && GLOBAL.tableTotalPage(dailyCheckinList.data.recordsTotal);
  const numbers = GLOBAL.tableNumber(payload.start);
  const openAnchor = Boolean(anchor);

  useEffect(() => {
    onResync();
  }, [payload]);

  return (
    <>
      <Content
        title="Daily Check-In"
        tableHeadTitles={tableHeadTitles}
        onSearch={onSearch}
        onResync={onResync}
        onAdditional={() => setDialogCreate(true)}
        additionalTitle="Create Check-In"
        additionalPrefixIcon="Create"
        pagination={onChangePage}
        onChangePerPage={onChangePerPage}
        totalPage={totalPage}
        totalRecords={dailyCheckinList.data?.recordsTotal}
        page={payload.start + 1}
        isLoading={dailyCheckinList.isLoading}
      >
        {dailyCheckinList.isSuccess && dailyCheckinList.data?.data?.map((row: DailyCheckinEventProps, index: number) => {
          const isStatusNotActive =  row.status === 'Not Started' || row.status === null;
          const isStatusActive = row.status === 'Active';
          const isStatusDisable = row.status === 'Disabled' || row.status === 'Ended';
         
          return (
          <TableRow key={index}>
            <TableCell>{numbers[index] + 1}</TableCell>
            <TableCell>
              <IconButton
                id={`button-${index}-${row.id}`}
                aria-haspopup="true"
                aria-controls={openAnchor ? `menu-${index}-${row.id}` : undefined}
                aria-expanded={openAnchor ? 'true' : undefined}
                onClick={(event) => onAction(event, index, row.id)}
              >
                <Icon icon="MoreHoriz" />
              </IconButton>
              <Menu
                  id={`menu-${index}-${row.id}`}
                  anchorEl={anchor}
                  open={openAnchor && menu === index}
                  onClose={() => onRemoveAnchor(() => setMenu(null))}
                >
                  { (isStatusDisable || !isStatusNotActive)  && (
                    <MenuItem onClick={() => onRemoveAnchor(() => setDialogDetail(true))}>
                      View Detail
                    </MenuItem>
                  )}

                  {!isStatusDisable && (
                    <MenuItem onClick={() => onRemoveAnchor(() => setDialogUpdate(true))}>
                      Edit
                    </MenuItem>
                  )}

                  {!isStatusDisable && !isStatusNotActive && (
                    <MenuModalItem
                      title='Disable'
                      loading={disableResult.isLoading}
                      onClick={() => onRemoveAnchor(() => selectedId && disableDailyCheckin(selectedId))}
                    >
                      <Typography color="error">Disable</Typography>
                    </MenuModalItem>
                  )}

                  {!isStatusDisable && !isStatusActive && (
                    <MenuModalItem
                      title='Delete'
                      loading={deleteResult.isLoading}
                      onClick={() => onRemoveAnchor(() => selectedId && deleteDailyCheckin(selectedId))}
                    >
                      <Typography color="error">Delete</Typography>
                    </MenuModalItem>
                  )}
                </Menu>
              <Edit
                id={row.id}
                isStatusActive={isStatusActive}
                open={dialogUpdate && menu === index}
                onClose={() => setDialogUpdate(false)}
              />
              <Detail
                id={row.id}
                open={dialogDetail && menu === index}
                onClose={() => setDialogDetail(false)}
              />
            </TableCell>
            {columns.map(col => (
              <TableCell key={col.key}>
                {col.render ? col.render(row) : row[col.key as keyof typeof row] || '-'}
              </TableCell>
            ))}
          </TableRow>
        );
      })}
      </Content>
      <Create open={dialogCreate} onClose={() => setDialogCreate(false)} />
      <Notification
        open={!deleteResult.isLoading && !deleteResult.isUninitialized}
        onClose={() => (deleteResult.isError ? deleteResult.reset() : location.reload())}
        isError={Boolean(deleteResult.error) && deleteResult.isError}
        message={GLOBAL.returnExceptionMessage(deleteResult.isError, deleteResult.error as ErrorProps)}
      />
      <Notification
        open={!disableResult.isLoading && !disableResult.isUninitialized}
        onClose={() => (disableResult.isError ? disableResult.reset() : location.reload())}
        isError={Boolean(disableResult.error) && disableResult.isError}
        message={GLOBAL.returnExceptionMessage(disableResult.isError, disableResult.error as ErrorProps)}
      />
    </>
  );
};

export default DailyCheckinList;
